import type { PayloadAction } from '@reduxjs/toolkit';
import type { BusinessInformation, PersonInformation } from '@/types/entities/entityInfo';
import type { SearchUsersResponse } from '@/types/entities/users';
import { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import type {
  CreateContactAutomaticallyBodyType,
  CreateContactManuallyRequestType,
  CreateContactSearchExistedUserByIdRequestType,
} from './types';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import {
  getBeneficiaryDetails,
  getContactList,
  getInsuredDetails,
  getProducerDetails,
  refreshContactList,
} from '@/features/entityDetails/store/entityDetailsSlice';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { getClaimSetV2 } from '@/domains/claimSets/store/claimSetSlice';
import {
  getCreateContactAutomatically,
  getCreateContactEntities,
  getCreateContactEntitiesSuccess,
  getCreateContactExistingUserById,
  getCreateContactExistingUserByIdSuccess,
  getCreateContactExistingUsers,
  getCreateContactExistingUsersSuccess,
  getCreateContactManually,
  resetCreateContactState,
  setAreCreateContactEntitiesLoading,
  setCreateContactAutomaticallyLoadingStatus,
  setCreateContactExistingUserByIdLoadingStatus,
  setCreateContactManuallyLoadingStatus,
  setUsersListForContactCreateLoadingStatus,
} from '@/domains/contacts/store/createContactSlice';
import { dispatchUpdatedEntityEvent } from '@/domains/contacts/utils/dispatchUpdatedEntityEvent';
import { ApiDomainPaths } from '@/enums/apiDomainPaths';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';
import { checkContactInformation } from '@/utils/checkContactInformation';
import { transformSearchUserByIdResponse } from '@/utils/converters/transformSearchUsersByIdResponse';
import { transformSearchUsersResponse } from '@/utils/converters/transformSearchUsersResponse';

function* fetchCreateContactExistingUsersSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(setUsersListForContactCreateLoadingStatus(true));
    const { data }: HttpServiceResponse<SearchUsersResponse> = yield call(
      HttpService.get,
      `${ApiDomainPaths.EXAMINER}/search/search-existing-users?searchExpression=${payload}`,
      {},
    );
    const transformedData = transformSearchUsersResponse(data);
    yield put(getCreateContactExistingUsersSuccess(transformedData));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setUsersListForContactCreateLoadingStatus(false));
  }
}

function* fetchCreateContactExistingUserByIdSaga({
  payload: { relationshipType, searchExpression, requestBody },
}: PayloadAction<CreateContactSearchExistedUserByIdRequestType>) {
  try {
    yield put(setCreateContactExistingUserByIdLoadingStatus(true));
    const { data }: HttpServiceResponse<Array<PersonInformation | BusinessInformation>> = yield call(
      HttpService.get,
      `/examiner/search/${relationshipType.toLowerCase()}?searchExpression=${searchExpression}`,
      {},
    );
    const transformedData = transformSearchUserByIdResponse(data, relationshipType);
    transformedData
      ? yield put(getCreateContactExistingUserByIdSuccess(transformedData))
      : yield put(getCreateContactManually({ requestBody }));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateContactExistingUserByIdLoadingStatus(false));
  }
}

function* createContactManuallySaga({
  payload: { requestBody, searchParams },
}: PayloadAction<CreateContactManuallyRequestType>) {
  const { relationship, contactHolder, contactHolderId } = requestBody;
  try {
    const currentClaimSetId = yield select((state: RootState) => state.claimSets.currentClaimSetId);

    yield put(setCreateContactManuallyLoadingStatus(true));

    const requestSearchParams = searchParams ? `?${searchParams}=true` : '';
    const { data }: HttpServiceResponse<string> = yield call(
      HttpService.post,
      `/examiner/contacts/create-${relationship.relationshipType.toLowerCase()}-contact-manually${requestSearchParams}`,
      { ...requestBody, contactInformation: checkContactInformation(requestBody.contactInformation) },
    );

    switch (contactHolder) {
      case EdsEntityCodes.Beneficiary: {
        yield put(getBeneficiaryDetails({ beneficiaryId: `${contactHolderId}` }));
        yield put(refreshContactList({ propertyName: EdsFilterEntities.BeneficiaryId, id: `${contactHolderId}` }));
        break;
      }
      case EdsEntityCodes.Insured: {
        yield put(getInsuredDetails({ insuredId: `${contactHolderId}` }));
        yield put(refreshContactList({ propertyName: EdsFilterEntities.InsuredId, id: `${contactHolderId}` }));
        break;
      }
    }

    if (requestBody?.relationship?.isReporter && currentClaimSetId) {
      yield put(getClaimSetV2(String(currentClaimSetId)));
    }

    yield put(getContactList());

    dispatchUpdatedEntityEvent({
      entityType: contactHolder as EdsEntityCodes,
      entityKey: contactHolderId,
      claimSetId: currentClaimSetId,
    });

    SnacksService.add({
      type: SnackType.Success,
      title: data,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateContactManuallyLoadingStatus(false));
    ModalService.close(Modals.AddContact);
    yield put(resetCreateContactState());
  }
}

function* createContactAutomaticallySaga({ payload }: PayloadAction<CreateContactAutomaticallyBodyType>) {
  const { contactHolder, contactHolderId } = payload;
  try {
    const currentClaimSetId = yield select((state: RootState) => state.claimSets.currentClaimSetId);

    yield put(setCreateContactAutomaticallyLoadingStatus(true));
    const { data }: HttpServiceResponse<string> = yield call(
      HttpService.post,
      `/examiner/contacts/create-contact-automatically`,
      payload,
    );

    switch (contactHolder) {
      case EdsEntityCodes.Beneficiary: {
        yield put(getBeneficiaryDetails({ beneficiaryId: `${contactHolderId}` }));
        yield put(refreshContactList({ propertyName: EdsFilterEntities.BeneficiaryId, id: `${contactHolderId}` }));
        break;
      }
      case EdsEntityCodes.Insured: {
        yield put(getInsuredDetails({ insuredId: `${contactHolderId}` }));
        yield put(refreshContactList({ propertyName: EdsFilterEntities.InsuredId, id: `${contactHolderId}` }));
        break;
      }
      case EdsEntityCodes.ProducerGroup:
      case EdsEntityCodes.Producer: {
        yield put(getProducerDetails({ id: `${contactHolderId}`, producerEntityType: contactHolder }));
        yield put(
          refreshContactList({
            propertyName:
              contactHolder === EdsEntityCodes.Producer
                ? EdsFilterEntities.ProducerId
                : EdsFilterEntities.ProducerGroupId,
            id: `${contactHolderId}`,
          }),
        );
        break;
      }
    }

    if (payload?.relationship?.isReporter && currentClaimSetId) {
      yield put(getClaimSetV2(String(currentClaimSetId)));
    }

    yield put(getContactList());

    dispatchUpdatedEntityEvent({
      entityType: contactHolder as EdsEntityCodes,
      entityKey: contactHolderId,
      claimSetId: currentClaimSetId,
    });

    SnacksService.add({
      type: SnackType.Success,
      title: data,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateContactAutomaticallyLoadingStatus(false));
    ModalService.close(Modals.AddContact);
    yield put(resetCreateContactState());
  }
}

export function* getCreateContactEntitiesSaga({ payload }: PayloadAction<number>) {
  try {
    yield put(setAreCreateContactEntitiesLoading(true));

    const { data } = yield call(
      HttpService.get,
      `examiner/contacts/${payload}/get-available-entities-for-contacts-creation`,
    );

    const entityOptions = data.availableEntities.map((item) => ({
      label: item.title,
      value: item.entityId,
      entityType: item.entityType,
    }));

    yield put(getCreateContactEntitiesSuccess(entityOptions));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setAreCreateContactEntitiesLoading(false));
  }
}

export function* createContactWatcher() {
  yield takeLatest(getCreateContactExistingUsers.type, fetchCreateContactExistingUsersSaga);
  yield takeLatest(getCreateContactExistingUserById.type, fetchCreateContactExistingUserByIdSaga);
  yield takeLatest(getCreateContactManually.type, createContactManuallySaga);
  yield takeLatest(getCreateContactAutomatically.type, createContactAutomaticallySaga);
  yield takeLatest(getCreateContactEntities.type, getCreateContactEntitiesSaga);
}
